
import React, { useState, useEffect, useRef, useCallback, useReducer, useMemo } from 'react'
import { CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import type { BaseNodeProps, CreatedItem } from '../types'
import { NodeFooter } from '../components/NodeFooter'
import { CreatedContent } from '../components/CreatedContent'
import { NodeError } from '../components/NodeError'
import { NodeProcessLog } from '../components/NodeProcessLog'
import { PromptTextarea } from '../components/ui'
import { NodeContainer } from './NodeContainer'
import { NODE_DIMENSIONS, NODE_VISUAL_CONSTANTS } from '@genieartinc/schemas'
import { CostBadge } from './CostBadge'
import { NodeState, NodeAction, nodeStateReducer } from './base-node-state'
import { arePropsEqual } from './base-node-comparator'
import { useBaseNodeImages } from './use-base-node-images'
import { CanvasFileManagerDialog } from './CanvasFileManagerDialog'
import { hydrateAttachments, planImageInputs } from '@/lib/canvas/image-input-capability'
import { PinnedParametersStrip } from '../pinned-parameters/PinnedParametersStrip'
import { useJobEstimate } from '@/hooks/useJobEstimate'

/** Stable empty references — avoid creating new [] / {} on every render */
const EMPTY_ARRAY: never[] = []
const EMPTY_OBJECT: Record<string, unknown> = {}

function RolePreviewWell({ preview }: { preview: NonNullable<BaseNodeProps['rolePreviews']>[number] }) {
  const [failed, setFailed] = useState(false)
  useEffect(() => { setFailed(false) }, [preview.url])
  const status = failed && preview.status === 'connected' ? 'error' : preview.status
  const message = status === 'error' && failed ? 'Couldn’t load this image.' : preview.message
  return (
    <div className="rounded-md bg-muted p-1.5 overflow-hidden" role="group" aria-label={preview.label}>
      <div className="mb-1 flex items-center gap-1.5 px-1">
        <Icon name="image" className="h-3 w-3 text-muted-foreground" />
        <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">{preview.label}</span>
      </div>
      {status === 'connected' && preview.url ? (
        <img src={preview.url} alt={preview.label} className="max-h-48 w-full rounded object-contain" onError={() => setFailed(true)} />
      ) : (
        <p className="px-1 py-2 text-[10px] text-muted-foreground" role="status">{message}</p>
      )}
    </div>
  )
}

function BaseNodeComponent({
  id,
  config,
  selected = false,
  width = NODE_DIMENSIONS.TextNode.width,
  height = NODE_DIMENSIONS.TextNode.height,
  initialData = {},
  onGenerate,
  onUpload,
  onUploadMultiple,
  onChange,
  onStateChange,
  onSelect,
  onHoverChange,
  children,
  headerActions,
  footerContent,
  loading,
  error,
  orchestratorState,
  connectedPrompt,
  connectedImage,
  rolePreviews,
  roleNote,
  canvasId,
  customContent,
  customContentPosition = 'before-output',
  costProvider = null,
  costModel = null,
  walletBalance,
  promptRequired = true,
  imageRequired = false,
  pinnedParameters,
  pinnedParameterValues,
  onPinnedParameterChange,
  pinnedModelName,
  pinnedModels,
  onPinnedModelChange,
  imageCapability,
  imageExecution = 'cloud',
  connectedImageUrls = EMPTY_ARRAY as unknown as string[],
}: BaseNodeProps) {
  // ⚡ Performance: useReducer batches multiple state updates
  const [state, dispatch] = useReducer(nodeStateReducer, {
    prompt: initialData.prompt || '',
    images: Array.isArray(initialData.images)
      ? initialData.images.filter(img => typeof img === 'string')
      : [],
    selectedItemId: undefined,
    uploadProgress: { uploading: false, current: 0, total: 0 },
    removingUrl: null,
    imageRoles: initialData.imageRoles ?? {},
    imageAssets: initialData.imageAssets ?? {},
  })

  // Track node ID to detect when different node is loaded
  const nodeIdRef = useRef(id)

  // Extract initial values to satisfy ESLint (only used when node ID changes)
  const initialPrompt = initialData.prompt
  const initialImages = initialData.images

  // Sync initialData when node ID changes (different node loaded)
  useEffect(() => {
    if (nodeIdRef.current === id) {
      // Same node - don't sync from initialData
      return
    }

    // Different node ID - reset state from initialData
    nodeIdRef.current = id
    dispatch({
      type: 'RESET',
      payload: {
        prompt: initialPrompt || '',
        images: Array.isArray(initialImages)
          ? initialImages.filter(img => typeof img === 'string')
          : [],
        imageRoles: initialData.imageRoles ?? {},
        imageAssets: initialData.imageAssets ?? {},
      },
    })
  }, [id, initialPrompt, initialImages])

  // Use createdItems from prop directly, not state (memoized to prevent handleItemDelete recreation)
  const createdItems = useMemo(() => initialData.createdItems || [], [initialData.createdItems])
  const cardRef = useRef<HTMLDivElement>(null)

  // Get locked state from initialData
  const isLocked = initialData.locked || false

  // Image upload/remove hook
  const { handleImageUpload, handleImageRemove, handleChooseFromFileManager, addPickedImages, browsingFiles, closeFileBrowser, isUploading } = useBaseNodeImages({
    id,
    canvasId,
    state,
    dispatch,
    onUpload,
    onUploadMultiple,
    capability: imageCapability,
  })

  // Stable onChange ref to avoid useEffect loops
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  // Track initial mount to skip first useEffect calls
  const isInitialMount = useRef(true)

  useEffect(() => {
    isInitialMount.current = false
  }, [])

  const handlePromptChange = useCallback((value: string) => {
    dispatch({ type: 'SET_PROMPT', payload: value })
  }, [])

  // ⚡ Performance: Batched useEffect with microtask deferral
  // Consolidates prompt + images changes into single callback (prevents double-firing)
  useEffect(() => {
    if (isInitialMount.current) return

    // Defer to next microtask to batch simultaneous state changes
    const cancelled = { current: false }
    queueMicrotask(() => {
      if (!cancelled.current) {
        onChangeRef.current?.({
          prompt: state.prompt,
          images: state.images,
          imageRoles: state.imageRoles,
          imageAssets: state.imageAssets,
        })
      }
    })

    return () => { cancelled.current = true }
  }, [state.prompt, state.images, state.imageRoles, state.imageAssets])

  const imagePlan = useMemo(() => {
    if (!imageCapability) return null
    const attachments = hydrateAttachments(state.images, state.imageAssets, state.imageRoles, connectedImageUrls)
    return planImageInputs(imageCapability, attachments, imageExecution)
  }, [connectedImageUrls, imageCapability, imageExecution, state.imageAssets, state.imageRoles, state.images])
  const imageInputBlocked = imagePlan?.blocked === true
  const modelRejectsImages = imageCapability?.declared === true && imageCapability.acceptsImages === false
  const moveImage = (url: string, direction: -1 | 1) => {
    const index = state.images.indexOf(url)
    const next = index + direction
    if (index < 0 || next < 0 || next >= state.images.length) return
    const images = state.images.slice()
    const [item] = images.splice(index, 1)
    images.splice(next, 0, item)
    dispatch({ type: 'SET_IMAGES', payload: images })
  }
  const assignRole = (url: string, role: string) => {
    dispatch({ type: 'SET_IMAGE_ROLES', payload: { ...state.imageRoles, [url]: role } })
  }

  const handleCreatedItemsChange = useCallback((items: CreatedItem[]) => {
    onChange?.({ createdItems: items })
  }, [onChange])

  const handleItemSelect = (item: CreatedItem) => {
    const newSelection = state.selectedItemId === item.id ? undefined : item.id
    dispatch({ type: 'SET_SELECTED_ITEM', payload: newSelection })
  }

  const handleItemDuplicate = (item: CreatedItem) => {
    const duplicatedItem: CreatedItem = {
      ...item,
      id: `${item.id}_duplicate_${Date.now()}`,
      metadata: {
        ...item.metadata,
        createdAt: new Date(),
        isGenerated: item.metadata?.isGenerated ?? true,
      },
    }
    const updatedItems = [...createdItems, duplicatedItem]
    handleCreatedItemsChange(updatedItems)
  }

  const handleItemDragOut = useCallback((item: CreatedItem) => {
    // Use functional update via onChange to get latest state
    onChange?.((prevState: Record<string, unknown>) => {
      const currentItems = (prevState?.createdItems || []) as CreatedItem[]
      const updatedItems = currentItems.filter((existingItem) => existingItem.id !== item.id)

      return {
        ...prevState,
        createdItems: updatedItems
      }
    })
  }, [onChange])

  const handleItemEdit = (itemId: string, newContent: string) => {
    const updatedItems = createdItems.map((item) =>
      item.id === itemId
        ? {
            ...item,
            content: newContent,
            metadata: { ...item.metadata, createdAt: new Date(), isGenerated: item.metadata?.isGenerated ?? true },
          }
        : item
    )
    handleCreatedItemsChange(updatedItems)
  }

  const handleItemDelete = useCallback((itemId: string) => {
    const updatedItems = createdItems.filter((item) => item.id !== itemId)
    handleCreatedItemsChange(updatedItems)
    if (state.selectedItemId === itemId) {
      dispatch({ type: 'SET_SELECTED_ITEM', payload: undefined })
    }
  }, [createdItems, state.selectedItemId, handleCreatedItemsChange])

  // Cmd+Enter shortcut: generate selected node
  useEffect(() => {
    if (!selected || !onGenerate) return
    const handler = () => onGenerate()
    document.addEventListener('genie:generate-selected', handler)
    return () => document.removeEventListener('genie:generate-selected', handler)
  }, [selected, onGenerate])

  // Handle keyboard events for delete functionality
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        selected &&
        state.selectedItemId &&
        (event.key === 'Delete' || event.key === 'Backspace')
      ) {
        const target = event.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          return
        }

        event.preventDefault()
        handleItemDelete(state.selectedItemId)
      }
    }

    if (selected && state.selectedItemId) {
      document.addEventListener('keydown', handleKeyDown)

      if (cardRef.current) {
        cardRef.current.focus()
      }

      return () => {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
    return undefined
  }, [selected, state.selectedItemId, handleItemDelete])

  // Pre-flight insufficient balance detection (disable generate button proactively)
  const { estimate } = useJobEstimate(costProvider, costModel)
  const hasInsufficientBalance = useMemo(() => {
    if (!walletBalance || !estimate) return false
    return parseFloat(walletBalance) < parseFloat(estimate.priceUsd)
  }, [walletBalance, estimate])

  // Dynamic border class based on orchestrator state
  const borderClass = React.useMemo(() => {
    const status = orchestratorState?.status;
    if (status === 'queued') {
      return 'border-amber-400 border-dashed animate-pulse';
    }
    if (status === 'working') {
      return 'border-green-500 animate-pulse';
    }
    if (status === 'error') {
      return 'border-red-500';
    }
    if (status === 'success') {
      return 'border-green-500';
    }
    return 'border-border';
  }, [orchestratorState?.status]);

  const isWorking = orchestratorState?.status === 'working' || orchestratorState?.status === 'queued';

  // Use custom title from initialData if available, otherwise use config title
  const displayTitle = initialData.title || config.title;

  // Wrap onChange to handle updater function format (for collapse/title changes)
  const handleStateChange = useCallback((updates: Record<string, unknown> | ((prev: Record<string, unknown>) => Record<string, unknown>)) => {
    if (typeof updates === 'function') {
      // Updater function - call with current initialData state
      const newState = updates(initialData);
      onChange?.(newState);
    } else {
      // Direct object - merge with existing state
      onChange?.(updates);
    }
  }, [onChange, initialData]);

  return (
    <>
      <NodeContainer
        nodeId={id}
        title={displayTitle}
        icon={config.icon}
        selected={selected}
        nodeType={config.type}
        width={width}
        onSelect={onSelect}
        onHoverChange={onHoverChange}
        onStateChange={onStateChange || handleStateChange}
        state={initialData}
        cardRef={cardRef}
        borderClass={borderClass}
      >
          <div className="absolute top-3 right-2 z-10">
            <div className="flex gap-1">
              {headerActions}
            </div>
          </div>

          <CardContent className="relative flex-1 pt-2 pr-2 pb-1 pl-2">
            {/* Connected text input from upstream node */}
            {connectedPrompt && (
              <div className="mb-2 rounded-md bg-muted px-2.5 py-2">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon name="link" className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Input</span>
                </div>
                <p className="text-xs text-foreground leading-relaxed line-clamp-4">{connectedPrompt}</p>
              </div>
            )}
            {rolePreviews && rolePreviews.length > 0 ? (
              <div className="mb-2 space-y-2">
                {rolePreviews.map((preview) => (
                  <RolePreviewWell key={`${preview.label}:${preview.url ?? preview.status}`} preview={preview} />
                ))}
                {roleNote && <p className="px-1 text-[10px] leading-snug text-muted-foreground">{roleNote}</p>}
              </div>
            ) : (connectedImageUrls.length > 0 || connectedImage) && (
              <div className="mb-2 rounded-md bg-muted p-1.5 overflow-hidden">
                <div className="flex items-center gap-1.5 mb-1 px-1">
                  <Icon name="image" className="h-3 w-3 text-muted-foreground" />
                  <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">Image Input</span>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {(connectedImageUrls.length > 0 ? connectedImageUrls : [connectedImage as string]).map(url => (
                    <img key={url} src={url} alt="Connected image" className="w-full max-h-48 object-contain rounded" />
                  ))}
                </div>
              </div>
            )}

            {config.fields.map((field) => (
              <div key={field.label}>
                {field.type === 'prompt' && (
                  <PromptTextarea
                    placeholder={connectedPrompt ? 'Write your instructions...' : field.placeholder}
                    value={state.prompt}
                    onChange={handlePromptChange}
                    onExecute={onGenerate}
                    disabled={isWorking || isLocked}
                    selected={selected}
                  />
                )}
              </div>
            ))}

            {customContentPosition === 'after-prompt' && customContent}

            {children}

            {/* Error Display */}
            {error && (
              <div className="px-4 py-2 text-sm text-destructive bg-destructive/10 rounded-md">
                {error}
              </div>
            )}
          </CardContent>

          {/* Pinned Parameters Strip — between prompt and upload/generate */}
          {((pinnedParameters && pinnedParameters.length > 0) || pinnedModelName) && onPinnedParameterChange && (
            <PinnedParametersStrip
              pinnedParameters={pinnedParameters ? pinnedParameters.map(p => p.name) : EMPTY_ARRAY}
              parameters={pinnedParameters || EMPTY_ARRAY}
              values={pinnedParameterValues || EMPTY_OBJECT}
              onChange={onPinnedParameterChange}
              modelName={pinnedModelName}
              models={pinnedModels}
              onModelChange={onPinnedModelChange}
            />
          )}

          {/* Uploaded Images Display with Action Buttons */}
          <div className="px-2 py-2">
            <div className="flex items-center gap-2">
              {/* Left column - Images grid (always present to maintain layout) */}
              <div className="flex-1">
                {state.uploadProgress.uploading && state.images.length === 0 ? (
                  <div className="flex items-center justify-center h-[80px] bg-muted rounded-md" role="status" aria-live="polite">
                    <div className="flex flex-col items-center gap-2">
                      <Icon name="loader" className="h-5 w-5 animate-spin text-muted-foreground" />
                      <div className="text-xs text-muted-foreground">
                        Uploading {state.uploadProgress.current} / {state.uploadProgress.total}
                      </div>
                    </div>
                  </div>
                ) : state.images.length > 0 ? (
                  <div className="grid grid-cols-3 gap-2">
                    {state.images.map((img, index) => (
                      <div
                        key={img}
                        className="group/image relative min-h-[80px] w-full overflow-hidden rounded-md bg-muted"
                      >
                        <img
                          src={img}
                          alt={`Attached image ${index + 1}`}
                          className="h-[80px] w-full object-cover"
                        />

                        {state.removingUrl === img && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md">
                            <Icon name="loader" className="h-5 w-5 animate-spin text-white" />
                          </div>
                        )}

                        <div className="absolute top-1 right-1 flex gap-0.5">
                          {state.images.length > 1 && (
                            <>
                              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 rounded-[6px] p-0 bg-card hover:bg-accent" aria-label={`Move image ${index + 1} earlier`} disabled={index === 0} onClick={(e) => { e.stopPropagation(); moveImage(img, -1) }}>
                                <Icon name="chevron-left" className="h-3 w-3" />
                              </Button>
                              <Button type="button" variant="ghost" size="sm" className="h-7 w-7 rounded-[6px] p-0 bg-card hover:bg-accent" aria-label={`Move image ${index + 1} later`} disabled={index === state.images.length - 1} onClick={(e) => { e.stopPropagation(); moveImage(img, 1) }}>
                                <Icon name="chevron-right" className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 rounded-[6px] p-0 bg-card hover:bg-accent"
                            aria-label={`Remove image ${index + 1}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleImageRemove(img)
                            }}
                            disabled={state.removingUrl === img}
                          >
                            <Icon name="x" className="h-3 w-3" />
                          </Button>
                        </div>
                        {imageCapability && imageCapability.slots.length > 1 && (
                          <select
                            aria-label={`Role for image ${index + 1}`}
                            className="absolute inset-x-1 bottom-1 h-6 rounded bg-card px-1 text-[10px]"
                            value={state.imageRoles[img] ?? ''}
                            onChange={event => assignRole(img, event.target.value)}
                            onClick={event => event.stopPropagation()}
                          >
                            <option value="">Choose role</option>
                            {imageCapability.slots.map(slot => <option key={slot.name} value={slot.name}>{slot.label}</option>)}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <NodeProcessLog orchestratorState={orchestratorState} nodeType={config.type} inline />
                )}
              </div>

              {/* Right column - Action buttons */}
              <TooltipProvider delayDuration={150}>
                <div className="relative flex items-center gap-1">
                  {/* Cost estimate badge */}
                  <CostBadge
                    provider={costProvider}
                    model={costModel}
                    balance={walletBalance}
                  />

                  {/* Upload button */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Upload images"
                        className="h-8 w-8 rounded-[8px] p-0"
                        onClick={handleImageUpload}
                        disabled={isWorking || state.uploadProgress.uploading || isLocked || modelRejectsImages || (!imageCapability && state.images.length >= NODE_VISUAL_CONSTANTS.limits.maxImages)}
                      >
                        {state.uploadProgress.uploading ? (
                          <Icon name="loader" className="h-4 w-4 animate-spin" />
                        ) : (
                          <Icon name="upload" className="h-4 w-4" />
                        )}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent size="sm">
                      {modelRejectsImages
                        ? 'This model does not accept images'
                        : !imageCapability && state.images.length >= NODE_VISUAL_CONSTANTS.limits.maxImages
                        ? `Maximum ${NODE_VISUAL_CONSTANTS.limits.maxImages} images allowed`
                        : isLocked
                        ? 'Node is locked'
                        : 'Upload images'}
                    </TooltipContent>
                  </Tooltip>

                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Choose from File Manager"
                        className="h-8 w-8 rounded-[8px] p-0"
                        onClick={() => void handleChooseFromFileManager()}
                        disabled={isWorking || state.uploadProgress.uploading || isLocked || modelRejectsImages || !canvasId}
                      >
                        <Icon name="folder-open" className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent size="sm">
                      {modelRejectsImages ? 'This model does not accept images' : 'Choose from File Manager'}
                    </TooltipContent>
                  </Tooltip>

                  {/* Generate button */}
                  {onGenerate && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="default"
                          size="sm"
                          aria-label="Generate"
                          aria-busy={isWorking}
                          className="h-8 w-8 rounded-[8px] p-0"
                          onClick={onGenerate}
                          disabled={
                            isWorking || isLocked || hasInsufficientBalance ||
                            (promptRequired && !state.prompt.trim() && !connectedPrompt) ||
                            (imageRequired && state.images.length === 0 && connectedImageUrls.length === 0 && !connectedImage) ||
                            imageInputBlocked
                          }
                        >
                          {(isWorking || isLocked) ? (
                            <Icon name="loader" className="h-4 w-4 animate-spin" />
                          ) : (
                            <Icon name="corner-down-left" className="h-4 w-4" />
                          )}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent size="sm">
                        {hasInsufficientBalance
                          ? 'Insufficient balance — top up to generate'
                          : isLocked
                          ? 'Node is locked'
                          : promptRequired && !state.prompt.trim() && !connectedPrompt
                          ? 'Enter a prompt first'
                          : imageInputBlocked
                          ? 'Resolve image inputs before running'
                          : imageRequired && state.images.length === 0 && connectedImageUrls.length === 0 && !connectedImage
                          ? 'Upload or connect an image first'
                          : 'Generate'}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
              </TooltipProvider>
            </div>
            {imagePlan?.explanation && (
              <p role="alert" className="mt-2 text-xs leading-relaxed text-destructive">{imagePlan.explanation}</p>
            )}
          </div>

          {customContentPosition === 'before-output' && customContent}

          {/* Created Content Section */}
          {createdItems && createdItems.length > 0 && (
            <div
              className="border-t border-border overflow-hidden"
              style={{
                borderBottomLeftRadius: `${NODE_VISUAL_CONSTANTS.borderRadius.card}px`,
                borderBottomRightRadius: `${NODE_VISUAL_CONSTANTS.borderRadius.card}px`
              }}
            >
              <CreatedContent
                items={createdItems}
                nodeType={config.type}
                selectedItemId={state.selectedItemId}
                onItemSelect={handleItemSelect}
                onItemDuplicate={handleItemDuplicate}
                onItemDragOut={handleItemDragOut}
                onItemEdit={handleItemEdit}
                onItemDelete={handleItemDelete}
                locked={isLocked}
              />
            </div>
          )}
        </NodeContainer>

        {/* Error display (outside container, bottom-left) */}
        <NodeError
          message={
            orchestratorState?.status === 'error'
              ? orchestratorState.message
              : undefined
          }
          onDismiss={() => {
            onChange?.({ orchestrator: { status: 'idle' } });
          }}
        />
        {browsingFiles && canvasId && (
          <CanvasFileManagerDialog
            canvasId={canvasId}
            multiple={imageCapability ? imageCapability.multiple : true}
            onPick={addPickedImages}
            onClose={closeFileBrowser}
          />
        )}
      </>
    )
}

export const BaseNode = React.memo(BaseNodeComponent, arePropsEqual)
